import { z } from 'zod'
import { zId } from './scalars'

/**
 * Enrolment.
 *
 * A kiosk trades a one-time code for a bearer token it puts in the machine's
 * keychain. Replaces the `licenseKey` the old app kept in `electron-store` and
 * asked an operator to type — which was also 1C's `stock_location_code`, and
 * had to be written atomically beside two QPay codes or the stores drifted.
 */
export const zEnrol = z.object({
  code: z.string().min(6).max(32),
  deviceName: z.string().min(1).max(64),
  platform: z.string().max(32).optional(),
  shellVersion: z.string().max(32).optional()
})

export const zEnrolment = z.object({
  deviceId: zId,
  /** Shown once. Stored by the shell in the OS keychain and never again by us. */
  token: z.string(),
  branchId: zId,
  branchName: z.string()
})

/** What the kiosk reports on every connect, so support can see the counter. */
export const zHeartbeat = z.object({
  shellVersion: z.string().max(32).optional(),
  platform: z.string().max(32).optional(),
  printerKind: z.string().max(32).optional(),
  /** Unacknowledged outbox entries. A number nobody wants to be non-zero. */
  outboxDepth: z.number().int().min(0).default(0)
})

export const zBranchConfig = z.object({
  branchId: zId,
  name: z.string(),
  address: z.string().nullable(),
  wifiName: z.string().nullable(),
  wifiPassword: z.string().nullable(),
  isClosed: z.boolean(),
  timezone: z.string(),
  /**
   * A self-service kiosk or a staffed POS till, set from the console. A POS
   * till always offers cash, and 1C files its sales with `ispos: "1"`.
   */
  mode: z.enum(['kiosk', 'pos']).default('kiosk'),
  /**
   * When a cached menu stops being trustworthy, in hours.
   *
   * `warn` puts a strip on the screen; `refuse` stops the shop selling.
   * `refuse: null` means an operator has decided this shop keeps trading on a
   * stale menu. `docs/offline.md` always said these were an operator's policy
   * and not a constant in the source — they simply were not, until now.
   */
  menuStale: z.object({ warnHours: z.number().int(), refuseHours: z.number().int().nullable() }),
  paymentMethods: z.array(
    z.object({ id: zId, method: z.enum(['cash', 'card', 'qpay']), label: z.string(), sortOrder: z.number().int() })
  )
})

export type BranchConfig = z.infer<typeof zBranchConfig>

/**
 * A diagnostic bundle.
 *
 * Bounded at the schema so a kiosk cannot post a day of logs into a table
 * somebody has to read; the server truncates again on the way in.
 */
export const zDeviceReport = z.object({
  kind: z.enum(['startup', 'crash', 'requested', 'periodic']),
  shellVersion: z.string().max(32).optional(),
  pageVersion: z.string().max(64).optional(),
  platform: z.string().max(32).optional(),
  printerKind: z.string().max(32).optional(),
  outboxDepth: z.number().int().min(0).default(0),
  problems: z.array(z.string().max(500)).max(40).default([]),
  lines: z
    .array(z.object({ at: z.string(), level: z.string().max(10), message: z.string().max(2000) }))
    .max(200)
    .default([])
})

export type DeviceReport = z.infer<typeof zDeviceReport>
